import { useEffect, useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AzureDevOpsIcon } from "@/components/icons";
import { toast } from "sonner";
import { Check, CheckCircle2, Copy } from "lucide-react";

interface AzureDevOpsPatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialOrganization?: string;
  initialDisplayName?: string;
  onConnected: () => void;
}

export function AzureDevOpsPatDialog({
  open,
  onOpenChange,
  initialOrganization = "",
  initialDisplayName = "",
  onConnected,
}: AzureDevOpsPatDialogProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [organization, setOrganization] = useState(initialOrganization);
  const [pat, setPat] = useState("");
  const [patExpiresAt, setPatExpiresAt] = useState("");
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [webhookPassword, setWebhookPassword] = useState("");
  const [connectResult, setConnectResult] = useState<{
    webhookUrl: string;
    webhookSecret: string;
    webhookPassword: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setOrganization(initialOrganization);
      setDisplayName(initialDisplayName);
    }
  }, [open, initialOrganization, initialDisplayName]);

  const connectPatMutation =
    trpc.integration.azureDevOps.connectPat.useMutation({
      onSuccess: (data) => {
        toast.success("Azure DevOps connection established");
        setConnectResult({
          webhookUrl: data.webhookUrl,
          webhookSecret: data.webhookSecret,
          webhookPassword: data.webhookPassword,
        });
        onConnected();
      },
      onError: (err) => {
        toastTrpcError(err, "Failed to connect Azure DevOps");
      },
    });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization.trim() || !pat.trim()) {
      toast.error("Please provide both organization and Personal Access Token");
      return;
    }

    connectPatMutation.mutate({
      organization: organization.trim(),
      pat: pat.trim(),
      patExpiresAt: patExpiresAt ? new Date(patExpiresAt).toISOString() : null,
      displayName: displayName.trim() || undefined,
      webhookSecret: webhookSecret.trim() || undefined,
      webhookPassword: webhookPassword.trim() || undefined,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    setConnectResult(null);
    setOrganization("");
    setPat("");
    setPatExpiresAt("");
    setDisplayName("");
    setWebhookSecret("");
    setWebhookPassword("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AzureDevOpsIcon className="h-5 w-5 text-sky-500 fill-current" />
            Connect Azure DevOps with PAT
          </DialogTitle>
          <DialogDescription>
            Provide an Azure DevOps organization and Personal Access Token with{" "}
            <code className="font-mono text-xs">Work Items (Read & Write)</code>{" "}
            scope.
          </DialogDescription>
        </DialogHeader>

        {connectResult ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-800 dark:text-emerald-300">
              <p className="font-medium flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                Connection established!
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Now configure Service Hooks in Azure DevOps to receive updates.
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  label: "Webhook URL (Service Hook endpoint)",
                  value: connectResult.webhookUrl,
                  field: "webhookUrl",
                },
                {
                  label: "Basic Auth Password",
                  value: connectResult.webhookPassword,
                  field: "webhookPassword",
                },
                {
                  label: "Header Secret (X-Webhook-Secret)",
                  value: connectResult.webhookSecret,
                  field: "webhookSecret",
                },
              ].map((item) => (
                <div key={item.field} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {item.label}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={item.value}
                      className="font-mono text-xs"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => handleCopy(item.value, item.field)}
                    >
                      {copiedField === item.field ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}

              <div className="rounded-lg bg-muted/60 p-3 text-xs space-y-1.5">
                <p className="font-medium text-foreground">
                  Azure DevOps Service Hook Setup Instructions:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Go to Project Settings → Service Hooks</li>
                  <li>Add Web Hooks subscription</li>
                  <li>
                    Create subscriptions for: <strong>Work item updated</strong>
                    , <strong>Work item deleted</strong>, and{" "}
                    <strong>Work item restored</strong>
                  </li>
                  <li>
                    Paste the Webhook URL, Basic Auth password and Header Secret
                  </li>
                </ol>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ado-org">Organization Name or URL</Label>
              <Input
                id="ado-org"
                placeholder="e.g. my-org or https://dev.azure.com/my-org"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ado-pat">Personal Access Token (PAT)</Label>
              <Input
                id="ado-pat"
                type="password"
                placeholder="Paste Azure DevOps PAT"
                value={pat}
                onChange={(e) => setPat(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Scope required:{" "}
                <code className="font-mono">Work Items (Read & Write)</code>.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ado-expiry">PAT Expiry Date</Label>
                <Input
                  id="ado-expiry"
                  type="date"
                  value={patExpiresAt}
                  onChange={(e) => setPatExpiresAt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ado-display">Display Name (Optional)</Label>
                <Input
                  id="ado-display"
                  placeholder="e.g. Main Org"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ado-webhook-secret">
                  Header Secret (Optional)
                </Label>
                <Input
                  id="ado-webhook-secret"
                  placeholder="Auto-generated if blank"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ado-webhook-pass">
                  Basic Auth Pass (Optional)
                </Label>
                <Input
                  id="ado-webhook-pass"
                  placeholder="Auto-generated if blank"
                  value={webhookPassword}
                  onChange={(e) => setWebhookPassword(e.target.value)}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={connectPatMutation.isPending}>
                {connectPatMutation.isPending ? "Connecting..." : "Connect"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
